import React, { useState, useEffect } from 'react';
import { get } from 'lodash';
import {
  ComponentLibraryLoader,
  useToast,
} from '@unite-us/app-components';
import { DashboardNavigationLink, LeftNavigationBar } from 'common/Navigation';
import { datadogRum } from '@datadog/browser-rum';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Spinner } from 'common/spinners';
import * as apiHooks from 'src/api/APIHooks';
import { coreApi, getAuthToken } from 'src/api/config';
import * as standardHooks from 'src/api/standardHooks';
import {
  isTasksListV2Enabled,
  hbh1667pfrUnifiedTaskEngine,
  scrn942ScreeningsResultsUpdates,
} from 'src/common/utils/FeatureFlags/flags';
import * as useFormSubmissions from 'src/components/FormSubmissions/useFormSubmissions';
import * as useEmployeeHooks from 'src/components/Organization/api/hooks/v1/employeeHooks';
import ScreeningDetail from 'src/components/Screenings/Detail/ScreeningDetail';
import ClientsDrawer from 'src/components/ClientsDrawer';
import { hasUserRole } from 'src/components/User/utils';
import { env } from 'src/config/env';
import getClientsDetailLink from 'src/common/utils/getClientsDetailLink';
import { browserHistory } from 'common/utils/browserHistory';
import './Tasks.scss';

const Workflows = ComponentLibraryLoader({
  loadFn: () => import('@unite-us/app-workflows-react'),
  component: 'Workflows',
  loadingApp: 'web-app',
  source: 'app-workflows-react',
  datadogRum,
});

const ScreeningDetailWrapper = ComponentLibraryLoader({
  loadFn: () => import('@unite-us/app-screenings'),
  component: 'ScreeningDetailWrapper',
  source: 'app-screenings',
  loadingApp: 'web-app',
  datadogRum,
});

const Tasks = (props) => {
  const {
    employeeId,
    providerId,
    canCreateTasks,
    canSeePage,
    router,
    location,
    hbh1667pfrUnifiedTaskEngineEnabled,
    scrn942ScreeningsResultsUpdatesEnabled,
  } = props;
  const authToken = getAuthToken();
  const { addToast } = useToast();

  if (!canSeePage) {
    router.push('/');
  }

  const queryModal = 'showTaskInputModal';
  const queryParams = new URLSearchParams(location.search);
  const isShowModal = queryParams.get(queryModal) === 'true';
  const [showTaskInputModal, setShowTaskInputModal] = useState(isShowModal);
  const currentLocation = location.state?.pathname;
  const [drawerClient, setDrawerClient] = useState(null);
  const [isClientsDrawerOpen, setIsClientsDrawerOpen] = useState(false);
  const flowsBaseUrl = hbh1667pfrUnifiedTaskEngineEnabled ? env.FLOWS_BASE_URL : env.WORKFLOWS_BASE_URL;

  const { pathname } = location;
  useEffect(() => {
    if (hbh1667pfrUnifiedTaskEngineEnabled) {
      // Redirect only the /tasks to /tasks/all
      if (pathname === '/tasks') {
        router.replace('/tasks/all');
      }
      // If flag is disabled, force everything under /tasks back to /tasks
    } else if (pathname !== '/tasks' && pathname.startsWith('/tasks')) {
      router.replace('/tasks');
    }
  }, [pathname, hbh1667pfrUnifiedTaskEngineEnabled, router]);

  const renderWorkflowsApp = () => (
    <Workflows
      {...props}
      appSettings={
        {
          env: {
            authToken,
            currentEmployeeId: employeeId,
            currentProviderId: providerId,
            getAuthToken,
          },
          endpoints: {
            core: env.CORE_API,
            flows: flowsBaseUrl,
          },
          flags: {
            isTasksListV2Enabled: canCreateTasks,
            hbh1667pfrUnifiedTaskEngineEnabled,
            scrn942ScreeningsResultsUpdatesEnabled,
          },
          manualTaskModal: {
            showTaskInputModal,
            setShowTaskInputModal,
            currentLocation,
          },
        }
      }
      hooks={{
        ...useFormSubmissions,
        ...apiHooks,
        ...standardHooks,
        ...useEmployeeHooks,
      }}
      components={{
        AssessmentDetailsView: ScreeningDetail,
        ScreeningsView: ScreeningDetailWrapper,
        Spinner,
      }}
      callbacks={{
        toast: {
          success: (message) => addToast({ message, type: 'success' }),
          error: (message) => addToast({ message, type: 'error' }),
          warn: (message) => addToast({ message, type: 'warning' }),
        },
        onClientNameClick: (client) => {
          setDrawerClient(client);
          if (client) {
            setIsClientsDrawerOpen(!isClientsDrawerOpen);
          } else {
            // Closes drawer when switching task
            setIsClientsDrawerOpen(false);
          }
        },
        getClientsDetailLink,
        pushInHistory: (url) => {
          browserHistory.push(url);
        },
      }}
      apis={{
        core: coreApi,
      }}
    />
  );

  return (
    <>
      {hbh1667pfrUnifiedTaskEngineEnabled ? (
        <div className="flex antialiased min-h-full -mx-container-padding">
          <LeftNavigationBar className="space-y-2">
            <>
              <h4 className="text-sm font-semibold pl-6 text-white">TASKS</h4>
              <DashboardNavigationLink
                key="all"
                to="/tasks/all"
                label="All Tasks"
                className="text-sm"
                data-testid="all-tasks-nav"
                isActive={router && router.isActive({ pathname: '/tasks/all' })}
              />
              <DashboardNavigationLink
                key="inbound-screening"
                to="/tasks/inbound-screenings"
                label="Inbound Screening Tasks"
                className="text-sm"
                data-testid="inbound-screening-tasks-nav"
                isActive={router && router.isActive({ pathname: '/tasks/inbound-screenings' })}
              />
              <DashboardNavigationLink
                key="user-created"
                to="/tasks/user-created"
                label="User-Created Tasks"
                className="text-sm"
                data-testid="user-created-tasks-nav"
                isActive={router && router.isActive({ pathname: '/tasks/user-created' })}
              />
            </>
          </LeftNavigationBar>
          {renderWorkflowsApp()}
        </div>
      ) : (
        renderWorkflowsApp()
      )}
      {drawerClient && (
        <ClientsDrawer
          open={isClientsDrawerOpen}
          contact={drawerClient}
          onClose={() => setIsClientsDrawerOpen(false)}
        />
      )}
    </>
  );
};

Tasks.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
    pathname: PropTypes.string.isRequired,
    state: PropTypes.shape({
      pathname: PropTypes.string,
    }),
  }).isRequired,
  employeeId: PropTypes.string.isRequired,
  providerId: PropTypes.string.isRequired,
  canCreateTasks: PropTypes.bool.isRequired,
  canSeePage: PropTypes.bool.isRequired,
  router: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
    isActive: PropTypes.func.isRequired,
  }).isRequired,
  hbh1667pfrUnifiedTaskEngineEnabled: PropTypes.bool.isRequired,
  scrn942ScreeningsResultsUpdatesEnabled: PropTypes.bool.isRequired,
};

function mapStateToProps(state) {
  const employeeId = get(state, 'globalState.currentEmployee.id', '');
  const providerId = get(state, 'globalState.currentProvider.group.id', '');
  const canCreateTasks = isTasksListV2Enabled(state);
  const hbh1667pfrUnifiedTaskEngineEnabled = hbh1667pfrUnifiedTaskEngine(state);
  const scrn942ScreeningsResultsUpdatesEnabled = scrn942ScreeningsResultsUpdates(state);
  const canSeePage = hasUserRole({
    groupId: state.session.groupId,
    name: 'Tasks',
    roleType: 'feature',
    user: state.user,
  });

  return {
    employeeId,
    providerId,
    canCreateTasks,
    canSeePage,
    hbh1667pfrUnifiedTaskEngineEnabled,
    scrn942ScreeningsResultsUpdatesEnabled,
  };
}

export default connect(mapStateToProps)(withRouter(Tasks));
